import DeviceHelper from './deviceHelper';
import SessionHelper from './sessionHelper';
import { normalizeAppData } from './normalizeAppData';
import { getFdsRequestHeader } from './designSystem';

export interface RetryWithHeadersResult {
  data: any;
  headers: {
    endpoint_id?: string | null;
    endpoint_type?: string | null;
    task_ref?: string | null;
    process_id?: string | null;
  };
}

const retryWithHeaders = async (): Promise<RetryWithHeadersResult | null> => {
  const deviceId = DeviceHelper.getOrCreateDeviceId();
  const sessionId = SessionHelper.getOrCreateSessionId();

  const response = await fetch(window.location.href, {
    headers: {
      'Fie': 'true',
      'Fc': 'web',
      'Fdi': deviceId,
      'Fsi': sessionId,
      // FE-4577 — this is the FIRST request the renderer makes itself. The SSR
      // shell that preceded it could carry no claim (a browser navigation sends
      // no custom headers), so the negotiation begins here.
      'Fds': await getFdsRequestHeader(),
    },
  });

  if (!response.ok) {
    throw new Error(`Retry failed: ${response.status} ${response.statusText}`);
  }

  // Extract relevant response headers for future usage
  const responseHeaders = {
    endpoint_id: response.headers.get('Fseid'),
    endpoint_type: response.headers.get('Fset'),
    task_ref: response.headers.get('Fref'),
    process_id: response.headers.get('Fspid'),
  };

  const wrapResult = (data: any): RetryWithHeadersResult | null => {
    // The retry can come back as the flat 0.22.x envelope, as the legacy
    // `{ code, data }` one, or as an init-script blob — normalize before the
    // caller (index.tsx) reads `data.mode` / `data.screen` off it.
    // Same-origin (`window.location.href`), so the response header is readable
    // here; `session_data.fds` still takes precedence inside the boundary.
    const normalized = normalizeAppData(data, { fdsHeader: response.headers.get('Fds') });
    if (!normalized) return null;
    return { data: normalized, headers: responseHeaders };
  };

  const contentType = response.headers.get('content-type')?.toLowerCase() ?? '';

  // Most retries return JSON directly; parse it first when possible.
  if (contentType.includes('application/json')) {
    return wrapResult(await response.json());
  }

  const html = await response.text();
  const trimmed = html.trim();

  // Some backends return JSON with an unexpected content-type.
  if (trimmed) {
    try {
      return wrapResult(JSON.parse(trimmed));
    } catch {
      // Continue with HTML init-script parsing fallback.
    }
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const initScript = doc.getElementById('init');
  if (initScript?.textContent) {
    const match = initScript.textContent.match(/window\.__APP_DATA__\s*=\s*([\s\S]*);?\s*$/);
    if (match?.[1]) {
      return wrapResult(JSON.parse(match[1].replace(/;\s*$/, '')));
    }
  }

  return null;
};

export default retryWithHeaders;
